import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Pin, Plus } from 'lucide-react'
import { toast } from 'sonner'

import Link from '@/routing/Link'
import { useRouter } from '@/routing/navigation'
import { Skeleton } from '@/components/ui/skeleton'
import { ThreadRowMenu } from '@/components/shared/ThreadRowMenu'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'

import { useSidebarThreads } from './useSidebarThreads'

import { SidebarRoomItem } from './useSidebarThreads'

/** Skeleton rows while the list loads — same row rhythm as the real list, never a spinner. */
const SKELETON_ROW_COUNT = 5

interface SidebarThreadsSectionProps {
  /** Current route, used to mark the open thread. */
  pathname: string
  /**
   * Called after a sidebar navigation is allowed to proceed. The mobile sheet
   * closes itself with it; the desktop rail passes nothing and stays put.
   */
  onNavigate?: () => void
  /**
   * Host exit guard, same contract as `Sidebar`: return `true` to claim the
   * navigation, in which case this section neither navigates nor notifies.
   */
  onNavigateAway?: (to: string) => boolean
}

/**
 * The live Recent rail under the nav block: knowledge threads and started chat
 * rooms, one row design for both. Deliberately not `ThreadCard`:
 * that row carries a preview, a timestamp and a citation count, all of which
 * are noise at 279px. One truncated title per row is the whole contract.
 */
export function SidebarThreadsSection(props: SidebarThreadsSectionProps) {
  const { pathname, onNavigate, onNavigateAway } = props

  const { t } = useTranslation('app')
  const {
    threads,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    renameThread,
    pinThread,
    deleteThread,
    renameRoom,
    deleteRoom,
  } = useSidebarThreads()
  const router = useRouter()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()

  const [showFade, setShowFade] = useState(false)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const updateFade = useCallback(() => {
    const el = scrollerRef.current
    if (!el) {
      setShowFade(false)
      return
    }
    const overflows = el.scrollHeight > el.clientHeight + 1
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 1
    setShowFade(overflows && !atBottom)
  }, [])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) {
      return
    }
    updateFade()
    el.addEventListener('scroll', updateFade, { passive: true })
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateFade)
    resizeObserver?.observe(el)
    return () => {
      el.removeEventListener('scroll', updateFade)
      resizeObserver?.disconnect()
    }
  }, [updateFade, threads.length, isLoading])

  useEffect(() => {
    const root = scrollerRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel || !hasMore) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMore()
        }
      },
      { root, rootMargin: '100px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadMore, threads.length, isLoadingMore])

  const handleNavigate = (to: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onNavigateAway?.(to) === true) {
      event.preventDefault()
      return
    }
    onNavigate?.()
  }

  async function handleRename(token: string, nextTitle: string) {
    try {
      await renameThread(token, nextTitle)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('threads.errors.renameFailed')
      toast.error(message)
    }
  }

  async function handlePin(token: string, pinned: boolean) {
    try {
      await pinThread(token, pinned)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        t(pinned ? 'threads.errors.pinFailed' : 'threads.errors.unpinFailed')
      toast.error(message)
    }
  }

  async function handleDelete(token: string) {
    try {
      await deleteThread(token)
      if (pathname === ROUTES.thread(token)) {
        router.replace(ROUTES.threads)
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('threads.errors.deleteFailed')
      toast.error(message)
    }
  }

  async function handleRenameRoom(token: string, nextTitle: string) {
    try {
      await renameRoom(token, nextTitle)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('conversations.renameFailed')
      toast.error(message)
    }
  }

  async function handleDeleteRoom(token: string) {
    try {
      await deleteRoom(token)
      if (pathname === ROUTES.chatRoom(token)) {
        router.replace(ROUTES.chats)
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('conversations.deleteFailed')
      toast.error(message)
    }
  }

  /** Untitled room: the twin's name for a 1:1, the group size otherwise. */
  function roomTitle(room: SidebarRoomItem): string {
    if (room.title) {
      return room.title
    }
    if (room.roomType === 'one_on_one' && room.firstParticipantName) {
      return room.firstParticipantName
    }
    return t('nav.recentRoomFallback', { count: room.participantCount })
  }

  const rowClassName = (isActive: boolean) =>
    cn(
      'flex min-h-7 items-center gap-2 rounded-[4px] px-2 py-1 pr-7 text-[14px] font-normal leading-5 transition-colors',
      isActive
        ? 'bg-[hsl(var(--sidebar-nav-active-bg))] text-[hsl(var(--sidebar-nav-active-fg))]'
        : 'text-sidebar-foreground/82 hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground',
    )

  const menuTriggerClassName = cn(
    'h-6 w-6 text-sidebar-foreground/60 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground',
    'opacity-0 group-hover/thread:opacity-100 group-focus-within/thread:opacity-100 data-[state=open]:opacity-100',
  )

  // Without Assistant the threads lobby is not there to open, so the header
  // leads to the conversations lobby and there is no new-thread shortcut.
  const seeAllHref = assistantPlatformEnabled ? ROUTES.threads : ROUTES.chats

  if (!isLoading && threads.length === 0) {
    return null
  }

  const listContent = isLoading ? (
    <div data-testid="sidebar-threads-loading">
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
        <div key={index} className="px-2 py-1.5">
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </div>
  ) : (
    <>
      <ul className="flex flex-col gap-0.5" aria-busy={isLoadingMore}>
        {threads.map((thread) => {
          if (thread.kind === 'room') {
            const roomHref = ROUTES.chatRoom(thread.token)
            const isRoomActive = pathname === roomHref
            const title = roomTitle(thread)

            return (
              <li key={`room:${thread.token}`} className="group/thread relative">
                <Link
                  href={roomHref}
                  data-testid={`sidebar-room-${thread.token}`}
                  aria-current={isRoomActive ? 'page' : undefined}
                  title={title}
                  onClick={handleNavigate(roomHref)}
                  className={rowClassName(isRoomActive)}
                >
                  <span className="min-w-0 flex-1 truncate">{title}</span>
                </Link>

                <div
                  className="absolute right-0.5 top-1/2 z-10 h-6 w-6 -translate-y-1/2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ThreadRowMenu
                    token={thread.token}
                    testIdPrefix="sidebar-room"
                    title={title}
                    renameTitle={t('roomPane.renameTitle')}
                    deleteTitle={t('nav.recentRoomDeleteTitle')}
                    onRename={(nextTitle) => void handleRenameRoom(thread.token, nextTitle)}
                    onDelete={() => void handleDeleteRoom(thread.token)}
                    triggerClassName={menuTriggerClassName}
                  />
                </div>
              </li>
            )
          }

          const href = ROUTES.thread(thread.token)
          const isActive = pathname === href
          const showMenu = thread.isOwner !== false

          return (
            <li key={`thread:${thread.token}`} className="group/thread relative">
              <Link
                href={href}
                data-testid={`sidebar-thread-${thread.token}`}
                aria-current={isActive ? 'page' : undefined}
                title={thread.title}
                onClick={handleNavigate(href)}
                className={rowClassName(isActive)}
              >
                <span className="min-w-0 flex-1 truncate">{thread.title}</span>
                {thread.isRunning && (
                  <span
                    role="status"
                    aria-label={t('nav.threadRunning')}
                    data-testid={`sidebar-thread-running-${thread.token}`}
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary motion-safe:animate-pulse"
                  />
                )}
              </Link>

              {/* Pin sits in the far-right slot. Owner rows overlay the "..."
                  menu on hover so the thumbtack and the trigger share a cell. */}
              {(thread.pinned || showMenu) && (
                <div
                  className="absolute right-0.5 top-1/2 z-10 h-6 w-6 -translate-y-1/2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {thread.pinned && (
                    <Pin
                      aria-hidden
                      data-testid={`sidebar-thread-pinned-${thread.token}`}
                      className={cn(
                        'pointer-events-none absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 text-sidebar-foreground/55',
                        showMenu &&
                          'group-hover/thread:opacity-0 group-focus-within/thread:opacity-0',
                      )}
                      strokeWidth={2}
                    />
                  )}
                  {showMenu && (
                    <ThreadRowMenu
                      token={thread.token}
                      testIdPrefix="sidebar-thread"
                      title={thread.title}
                      isPinned={thread.pinned}
                      onRename={(nextTitle) => void handleRename(thread.token, nextTitle)}
                      onPin={() => void handlePin(thread.token, !thread.pinned)}
                      onDelete={() => void handleDelete(thread.token)}
                      triggerClassName={menuTriggerClassName}
                    />
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {isLoadingMore && (
        <div data-testid="sidebar-threads-loading-more" className="px-2 py-1.5">
          <Skeleton className="h-4 w-full" />
        </div>
      )}
      {hasMore && (
        <div
          ref={sentinelRef}
          data-testid="sidebar-threads-load-more"
          aria-hidden
          className="h-px"
        />
      )}
    </>
  )

  return (
    <section
      data-testid="sidebar-threads-section"
      aria-label={t('nav.threads')}
      className="flex min-h-0 flex-1 flex-col px-1 pt-3"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 px-2 pb-1">
        <Link
          href={seeAllHref}
          data-testid="sidebar-threads-see-all"
          title={assistantPlatformEnabled ? t('nav.browseAllThreads') : t('conversations.title')}
          onClick={handleNavigate(seeAllHref)}
          className="-ml-1 flex min-w-0 items-center gap-0.5 rounded-[4px] px-1 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/50 transition-colors hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground/80"
        >
          <span className="truncate">{t('nav.recent')}</span>
          <ChevronRight className="h-3 w-3 shrink-0" strokeWidth={2} />
        </Link>
        {assistantPlatformEnabled && (
          <Link
            href={ROUTES.threads}
            data-testid="sidebar-new-thread"
            aria-label={t('nav.newThread')}
            onClick={handleNavigate(ROUTES.threads)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] text-sidebar-foreground/60 transition-colors hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground"
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        )}
      </div>

      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollerRef}
          data-testid="sidebar-threads-scroller"
          className="h-full min-h-0 overflow-y-auto overscroll-contain"
        >
          {listContent}
        </div>
        {showFade && (
          <div
            data-testid="sidebar-threads-fade"
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-sidebar to-transparent"
          />
        )}
      </div>
    </section>
  )
}
